import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { doc, updateDoc } from "firebase/firestore";
import { updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import Header from "../components/Header";

export default function Settings() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  // Display name
  const [name, setName] = useState(userProfile?.name || "");
  const [savingName, setSavingName] = useState(false);

  // Email
  const [newEmail, setNewEmail] = useState(currentUser?.email || "");
  const [savingEmail, setSavingEmail] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  // Which section is expanded
  const [openSection, setOpenSection] = useState("name");

  function toggleSection(id) {
    setOpenSection((prev) => (prev === id ? null : id));
  }

  async function saveName() {
    if (!name.trim()) return alert("Name cannot be empty.");
    setSavingName(true);
    try {
      await updateDoc(doc(db, "users", currentUser.uid), { name: name.trim() });
      alert("Name updated!");
    } catch {
      alert("Failed to update name. Try again.");
    }
    setSavingName(false);
  }

  async function saveEmail() {
    if (!newEmail.trim()) return alert("Email cannot be empty.");
    if (newEmail === currentUser.email) return alert("That's already your current email.");
    setSavingEmail(true);
    try {
      await updateEmail(currentUser, newEmail.trim());
      alert("Email updated!");
    } catch (err) {
      // Firebase throws this specific code when the session is too old
      if (err.code === "auth/requires-recent-login") {
        alert("For security, changing your email requires a recent login. Please log out and sign back in, then try again.");
      } else {
        alert("Failed to update email. Make sure the address is valid and try again.");
      }
    }
    setSavingEmail(false);
  }

  async function savePassword() {
    if (!currentPassword) return alert("Please enter your current password.");
    if (!newPassword) return alert("Please enter a new password.");
    if (newPassword.length < 6) return alert("New password must be at least 6 characters.");
    if (newPassword !== confirmPassword) return alert("New passwords don't match.");

    setSavingPassword(true);
    try {
      // Firebase requires re-authentication before changing password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      alert("Password updated!");
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        alert("Current password is incorrect.");
      } else {
        alert("Failed to update password. Try again.");
      }
    }
    setSavingPassword(false);
  }

  return (
    <div style={styles.container}>
      <Header />

      <div style={styles.pageHeader}>
        <button style={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <div style={styles.headerText}>
          <h2 style={styles.title}>Account Settings</h2>
          <p style={styles.subtitle}>{currentUser?.email}</p>
        </div>
      </div>

      <div style={styles.content}>

        {/* Display Name */}
        <div style={styles.section}>
          <button
            style={styles.sectionHeader}
            onClick={() => toggleSection("name")}
          >
            <span style={styles.sectionTitle}>👤 Display Name</span>
            <span style={styles.chevron}>{openSection === "name" ? "▲" : "▼"}</span>
          </button>
          {openSection === "name" && (
            <div style={styles.sectionBody}>
              <p style={styles.currentValue}>Current: <strong>{userProfile?.name || "—"}</strong></p>
              <label style={styles.label}>New Name</label>
              <input
                style={styles.input}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <button style={styles.saveBtn} onClick={saveName} disabled={savingName}>
                {savingName ? "Saving..." : "Save Name"}
              </button>
            </div>
          )}
        </div>

        {/* Email */}
        <div style={styles.section}>
          <button
            style={styles.sectionHeader}
            onClick={() => toggleSection("email")}
          >
            <span style={styles.sectionTitle}>✉️ Email Address</span>
            <span style={styles.chevron}>{openSection === "email" ? "▲" : "▼"}</span>
          </button>
          {openSection === "email" && (
            <div style={styles.sectionBody}>
              <p style={styles.currentValue}>Current: <strong>{currentUser?.email}</strong></p>
              <div style={styles.warningBox}>
                <p style={styles.warningText}>
                  ⚠️ Changing your email requires a recent login. If it fails, log out and sign back in first.
                </p>
              </div>
              <label style={styles.label}>New Email</label>
              <input
                style={styles.input}
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              <button style={styles.saveBtn} onClick={saveEmail} disabled={savingEmail}>
                {savingEmail ? "Saving..." : "Save Email"}
              </button>
            </div>
          )}
        </div>

        {/* Password */}
        <div style={styles.section}>
          <button
            style={styles.sectionHeader}
            onClick={() => toggleSection("password")}
          >
            <span style={styles.sectionTitle}>🔒 Change Password</span>
            <span style={styles.chevron}>{openSection === "password" ? "▲" : "▼"}</span>
          </button>
          {openSection === "password" && (
            <div style={styles.sectionBody}>
              <label style={styles.label}>Current Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <label style={styles.label}>New Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="At least 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <label style={styles.label}>Confirm New Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button style={styles.saveBtn} onClick={savePassword} disabled={savingPassword}>
                {savingPassword ? "Saving..." : "Update Password"}
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8" },

  pageHeader: { backgroundColor: "white", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.05)" },
  backBtn: { padding: "8px 14px", backgroundColor: "#e2e8f0", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, whiteSpace: "nowrap" },
  headerText: { flex: 1 },
  title: { fontSize: 18, color: "#2d3748", margin: "0 0 2px 0" },
  subtitle: { fontSize: 13, color: "#718096", margin: 0 },

  content: { padding: 20, maxWidth: 600, margin: "0 auto" },

  section: { backgroundColor: "white", borderRadius: 12, marginBottom: 12, boxShadow: "0 2px 4px rgba(0,0,0,0.06)", overflow: "hidden" },
  sectionHeader: { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px", background: "none", border: "none", cursor: "pointer", boxSizing: "border-box" },
  sectionTitle: { fontSize: 16, fontWeight: "bold", color: "#2d3748" },
  chevron: { fontSize: 12, color: "#a0aec0" },
  sectionBody: { padding: "0 20px 20px 20px", borderTop: "1px solid #f0f4f8" },

  currentValue: { fontSize: 14, color: "#718096", margin: "14px 0 16px 0" },
  warningBox: { backgroundColor: "#fffaf0", border: "1px solid #f6e05e", borderRadius: 8, padding: "10px 14px", marginBottom: 16 },
  warningText: { fontSize: 13, color: "#744210", margin: 0, lineHeight: 1.5 },

  label: { display: "block", fontWeight: "bold", color: "#4a5568", marginBottom: 6, fontSize: 14 },
  input: { width: "100%", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 15, boxSizing: "border-box", marginBottom: 14 },
  saveBtn: { width: "100%", padding: 13, backgroundColor: "#4299e1", color: "white", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },
};